/**
 * New Relic monitoring utilities for MathJax rendering
 */

/// <reference path="../types/global.d.ts" />

interface NewRelicBrowser {
  addPageAction: (name: string, attributes?: Record<string, any>) => void;
  addRelease: (releaseId: string, versionId: string) => void;
  noticeError: (error: Error, customAttributes?: Record<string, any>) => void;
  setCustomAttribute: (key: string, value: boolean | number | string) => void;
}

declare global {
  interface Window {
    newrelic?: NewRelicBrowser;
  }
}

/**
 * Tracks MathJax initialization events
 */
export const trackMathJaxInitialization = {
  failure: (
    source: 'cdn' | 'npm',
    error: Error,
    attemptedFallback: boolean = false
  ) => {
    if (typeof window !== 'undefined' && window.newrelic) {
      const errorDetails = {
        attempted_fallback: attemptedFallback,
        error_message: error.message,
        error_stack: error.stack,
        source,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      window.newrelic.noticeError(error, errorDetails);
      window.newrelic.addPageAction(
        'mathjax_initialization_failure',
        errorDetails
      );

      console.error(
        '❌ New Relic: MathJax initialization failure tracked',
        errorDetails
      );
    }
  },

  success: (source: 'cdn' | 'npm', loadTime: number) => {
    if (typeof window !== 'undefined' && window.newrelic) {
      window.newrelic.addPageAction('mathjax_initialization_success', {
        loadTime,
        source,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
      });

      window.newrelic.setCustomAttribute('mathjax_source', source);
      window.newrelic.setCustomAttribute('mathjax_load_time', loadTime);
    }
  },
};

/**
 * Tracks individual formula rendering events
 */
export const trackFormulaRendering = {
  failure: (error: Error, formulaContent?: string, componentType?: string) => {
    if (typeof window !== 'undefined' && window.newrelic) {
      const errorDetails = {
        component_type: componentType || 'unknown',
        error_message: error.message,
        error_stack: error.stack,
        formula_content: formulaContent?.substring(0, 100) || 'unknown', // Limit content length
        page_url: window.location.pathname,
        timestamp: Date.now(),
        user_agent: navigator.userAgent,
      };

      window.newrelic.noticeError(error, errorDetails);
      window.newrelic.addPageAction(
        'mathjax_formula_render_failure',
        errorDetails
      );

      console.error(
        '❌ New Relic: Formula rendering failure tracked',
        errorDetails
      );
    }
  },

  slowRendering: (
    renderTime: number,
    threshold: number = 2000,
    formulaCount: number
  ) => {
    if (
      typeof window !== 'undefined' &&
      window.newrelic &&
      renderTime > threshold
    ) {
      window.newrelic.addPageAction('mathjax_slow_rendering', {
        formula_count: formulaCount,
        page_url: window.location.pathname,
        performance_ratio: renderTime / formulaCount, // Time per formula
        render_time: renderTime,
        threshold,
        timestamp: Date.now(),
      });

      console.warn('⚠️ New Relic: Slow MathJax rendering detected', {
        formulaCount,
        renderTime,
        threshold,
      });
    }
  },

  success: (
    formulaCount: number,
    renderTime: number,
    componentType?: string
  ) => {
    if (typeof window !== 'undefined' && window.newrelic) {
      window.newrelic.addPageAction('mathjax_formula_render_success', {
        component_type: componentType || 'unknown',
        formula_count: formulaCount,
        page_url: window.location.pathname,
        render_time: renderTime,
        timestamp: Date.now(),
      });

      window.newrelic.setCustomAttribute(
        'last_formula_render_time',
        renderTime
      );
      window.newrelic.setCustomAttribute(
        'total_formulas_rendered',
        formulaCount
      );
    }
  },
};

/**
 * Tracks MathJax re-rendering events (for route changes)
 */
export const trackMathJaxRerendering = {
  failure: (error: Error) => {
    if (typeof window !== 'undefined' && window.newrelic) {
      const errorDetails = {
        error_message: error.message,
        error_stack: error.stack,
        page_url: window.location.pathname,
        timestamp: Date.now(),
      };

      window.newrelic.noticeError(error, errorDetails);
      window.newrelic.addPageAction('mathjax_rerender_failure', errorDetails);

      console.error(
        '❌ New Relic: MathJax re-rendering failure tracked',
        errorDetails
      );
    }
  },

  success: (formulaCount: number, renderTime: number) => {
    if (typeof window !== 'undefined' && window.newrelic) {
      window.newrelic.addPageAction('mathjax_rerender_success', {
        formula_count: formulaCount,
        page_url: window.location.pathname,
        render_time: renderTime,
        timestamp: Date.now(),
      });
    }
  },
};

/**
 * Sets up custom attributes for the page
 */
export const setMathJaxPageAttributes = (
  conceptId?: string,
  formulaCount?: number
) => {
  if (typeof window !== 'undefined' && window.newrelic) {
    if (conceptId) {
      window.newrelic.setCustomAttribute('concept_id', conceptId);
    }

    if (formulaCount !== undefined) {
      window.newrelic.setCustomAttribute('page_formula_count', formulaCount);
    }

    window.newrelic.setCustomAttribute('mathjax_available', !!window.MathJax);
    window.newrelic.setCustomAttribute(
      'mathjax_ready',
      !!window.__MATHJAX_READY__
    );
  }
};

/**
 * Creates a performance timer for MathJax operations
 */
export const createMathJaxTimer = () => {
  const startTime = performance.now();

  return {
    end: () => performance.now() - startTime,
    endAndTrack: (eventType: string, additionalData?: Record<string, any>) => {
      const duration = performance.now() - startTime;

      if (typeof window !== 'undefined' && window.newrelic) {
        window.newrelic.addPageAction(`mathjax_${eventType}_timing`, {
          duration,
          timestamp: Date.now(),
          ...additionalData,
        });
      }

      return duration;
    },
  };
};
